import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';

import {
  APP_EVENTS,
  captureAppEvent,
  LOGOUT_TRIGGER,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  CustomModal,
  CustomModalBody,
  CustomModalFooter,
  CustomModalHeader,
  CustomModalTitle,
} from 'src/components/custom_components/CustomModal';

import { I18NextNS } from 'src/models/Language.model';

import { getAutoLogoutTiming } from 'src/utils/autoLogoutTiming';

const { timeout, promptBeforeIdle } = getAutoLogoutTiming(
  import.meta.env.VITE_AUTO_LOGOUT_MINUTES,
  import.meta.env.VITE_AUTO_LOGOUT_PROMPT_SECONDS,
);

export default function CustomIdleTimer() {
  const [remaining, setRemaining] = useState<number>(Math.ceil(timeout / 1000));
  const [open, setOpen] = useState<boolean>(false);

  const { logOut } = useRemoteBoundStore();

  const onIdle = () => {
    setOpen(false);
    captureAppEvent(APP_EVENTS.USER_LOGGED_OUT, {
      trigger: LOGOUT_TRIGGER.IDLE,
    });
    logOut();
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    setOpen(true);
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const handleStillHere = () => {
    activate();
  };

  const { t } = useTranslation(I18NextNS.PAGES);

  return (
    <CustomModal show={open}>
      <CustomModalHeader>
        <CustomModalTitle>{t('loginLogout.idleTimer.title')}</CustomModalTitle>
      </CustomModalHeader>
      <CustomModalBody>
        {t('loginLogout.idleTimer.desc', { remaining })}
      </CustomModalBody>
      <CustomModalFooter>
        <Button
          variant="primary"
          onClick={handleStillHere}
          data-track="modal.idle-timer.keep-logged-in"
          data-observe
        >
          {t('loginLogout.idleTimer.keepMeLoggedIn')}
        </Button>
      </CustomModalFooter>
    </CustomModal>
  );
}
